/** Mensaje genérico de login cuando no hay detalle seguro para mostrar. */
export const GENERIC_LOGIN_ERROR = 'Error de conexión';

/**
 * El Origin no corresponde a ningún dominio configurado (p.ej. demo.app.tr.inprovider.com):
 * el backend responde 404 con `error.code = 'DOMAIN_NOT_CONFIGURED'` antes de tocar la base.
 */
export const DOMAIN_NOT_CONFIGURED_CODE = 'DOMAIN_NOT_CONFIGURED';
export const DOMAIN_NOT_CONFIGURED_MESSAGE =
	'Esta dirección no corresponde a ninguna cuenta. Revisa la URL con la que ingresas o contacta a soporte.';

export function isDomainNotConfiguredError(error: unknown): boolean {
	return getApiErrorCode(error) === DOMAIN_NOT_CONFIGURED_CODE;
}

/**
 * Traduce errores HTTP del login a un mensaje visible para el usuario.
 *
 * Alcance deliberadamente estrecho: solo el **429** de rate limit se traduce a un
 * mensaje descriptivo. Cualquier otro status (incluye 500/502 con HTML o detalle
 * interno) cae en {@link GENERIC_LOGIN_ERROR} para no filtrar texto no pensado para el UI.
 *
 * Body esperado en 429:
 * `{ error: { message: "Rate limit exceeded. Try again in N seconds" } }`
 */
export function resolveLoginErrorMessage(error: unknown): string {
	const status = getHttpStatus(error);

	if (status === 429) {
		return formatRateLimitMessage(getApiErrorMessage(error));
	}

	// Por código, no por status: otro 404 no debe decir "dirección incorrecta". El mensaje
	// del API es texto de producto (nombra la dirección), no detalle interno.
	if (isDomainNotConfiguredError(error)) {
		return getApiErrorMessage(error) ?? DOMAIN_NOT_CONFIGURED_MESSAGE;
	}

	return GENERIC_LOGIN_ERROR;
}

/**
 * Diálogo de recuperar contraseña: si el rechazo HTTP es DOMAIN_NOT_CONFIGURED devuelve el
 * mensaje claro; si no, null y el diálogo usa su texto genérico. Antes el diálogo descartaba
 * el error y siempre decía "No pudimos procesar la solicitud" (caso real TR, 16-oct).
 */
export function recoverRejectionMessage(error: unknown): string | null {
	return isDomainNotConfiguredError(error) ? resolveLoginErrorMessage(error) : null;
}

/**
 * Variante OAuth: si el error es el genérico, usa el fallback del proveedor
 * (Google/Microsoft); si es rate limit u otro mensaje específico, lo respeta.
 */
export function resolveOAuthLoginErrorMessage(error: unknown, providerFallback: string): string {
	const message = resolveLoginErrorMessage(error);
	return message === GENERIC_LOGIN_ERROR ? providerFallback : message;
}

function getHttpStatus(error: unknown): number | null {
	if (!error || typeof error !== 'object') {
		return null;
	}
	const status = (error as { status?: unknown }).status;
	return typeof status === 'number' ? status : null;
}

/**
 * Extrae `error.message` del body JSON del API.
 * Si el body es string no-JSON (p. ej. HTML de un proxy), no lo devuelve: evita
 * mostrar ruido técnico en el login.
 */
function getApiErrorMessage(error: unknown): string | null {
	if (!error || typeof error !== 'object') {
		return null;
	}

	const body = (error as { error?: unknown }).error;
	if (!body) {
		return null;
	}

	if (typeof body === 'string') {
		try {
			return extractNestedMessage(JSON.parse(body));
		} catch {
			return null;
		}
	}

	if (typeof body === 'object') {
		return extractNestedMessage(body);
	}

	return null;
}

function getApiErrorCode(error: unknown): string | null {
	if (!error || typeof error !== 'object') {
		return null;
	}
	let body = (error as { error?: unknown }).error;
	if (typeof body === 'string') {
		try {
			body = JSON.parse(body);
		} catch {
			return null;
		}
	}
	if (!body || typeof body !== 'object') {
		return null;
	}
	const code = (body as { error?: { code?: unknown } }).error?.code;
	return typeof code === 'string' ? code : null;
}

function extractNestedMessage(body: unknown): string | null {
	if (!body || typeof body !== 'object') {
		return null;
	}
	const nested = body as { error?: { message?: unknown }; message?: unknown };
	const message = nested.error?.message ?? nested.message;
	return typeof message === 'string' && message.trim() ? message.trim() : null;
}

function formatRateLimitMessage(apiMessage: string | null): string {
	const seconds = parseRetrySeconds(apiMessage);
	if (seconds == null) {
		return 'Demasiados intentos de inicio de sesión. Intenta de nuevo más tarde.';
	}

	const unit = seconds === 1 ? 'segundo' : 'segundos';
	return `Demasiados intentos de inicio de sesión. Intenta de nuevo en ${seconds} ${unit}.`;
}

function parseRetrySeconds(message: string | null): number | null {
	if (!message) {
		return null;
	}
	const match = message.match(/(\d+)\s*seconds?/i);
	if (!match) {
		return null;
	}
	const seconds = Number(match[1]);
	return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}
